import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import {
  peerScoreDetailStudent,
  peerScoreGivenList,
  peerScoreReceivedList,
} from '../data/peerReviewDetailData';

// 学生互评打分明细抽屉
// 用于“互评任务管理-查看打分明细”和“恶意打分记录-查看明细”两个入口。
export default function PeerScoringDetailDrawer({
  onClose,
  showReceived = true,
  title = '打分明细查看',
  isMaliciousOverride,
  onMaliciousChange,
}) {
  // ================= 页面状态 =================
  const [isCorrectionModalOpen, setIsCorrectionModalOpen] = useState(false);
  const [isCurrentMalicious, setIsCurrentMalicious] = useState(
    isMaliciousOverride ?? peerScoreDetailStudent.isMalicious
  );

  useEffect(() => {
    setIsCurrentMalicious(isMaliciousOverride ?? peerScoreDetailStudent.isMalicious);
  }, [isMaliciousOverride]);

  // ================= 恶意打分阈值判断 =================
  const lowScoreRateValue = Number(String(peerScoreDetailStudent.lowScoreRate).replace('%', ''));
  const canCorrectMaliciousStatus = lowScoreRateValue > 50;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/35">
      <div
        className="h-full bg-[#f3f6fa] shadow-[-8px_0_24px_rgba(0,0,0,0.2)] flex flex-col"
        style={{ width: 'min(980px, calc((100vw - 240px) * 0.56))' }}
      >
        {/* 抽屉标题栏 */}
        <div className="h-[58px] px-6 bg-white border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-1 h-5 bg-[#1890ff] rounded" />
            <h2 className="text-[16px] font-bold text-gray-900">
              {title} - {peerScoreDetailStudent.name}（{peerScoreDetailStudent.studentId}）
            </h2>
          </div>

          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* 基本信息卡片：展示学生和互评统计信息 */}
          <BasicInfoCard student={peerScoreDetailStudent} />

          {/* TA 的打分情况卡片：展示 TA 给别人打出的分数 */}
          <ScoreDetailCard
            title="TA的打分情况（评别人）"
            description={`以下列出由 ${peerScoreDetailStudent.name} 对其他同学的打分明细：`}
            idHeader="被评人学号"
            nameHeader="被评人姓名"
            scoreHeader="给出分值"
            rows={peerScoreGivenList}
            isMalicious={isCurrentMalicious}
            canCorrectMaliciousStatus={canCorrectMaliciousStatus}
            onOpenCorrection={() => setIsCorrectionModalOpen(true)}
          />

          {/* TA 的得分情况卡片：互评任务管理中展示，恶意打分记录页不展示 */}
          {showReceived && (
            <ScoreDetailCard
              title="TA的得分情况（被别人评）"
              description={`以下列出其他同学对 ${peerScoreDetailStudent.name} 的打分明细：`}
              idHeader="评价人学号"
              nameHeader="评价人姓名"
              scoreHeader="获得分值"
              rows={peerScoreReceivedList}
            />
          )}
        </div>
      </div>

      {/* 人工修正弹窗 */}
      {isCorrectionModalOpen && (
        <MaliciousCorrectionModal
          currentIsMalicious={isCurrentMalicious}
          onClose={() => setIsCorrectionModalOpen(false)}
          onConfirm={(nextValue) => {
            const nextIsMalicious = nextValue === '是';
            setIsCurrentMalicious(nextIsMalicious);
            if (onMaliciousChange) {
              onMaliciousChange(nextIsMalicious);
            }
            setIsCorrectionModalOpen(false);
          }}
        />
      )}
    </div>
  );
}

// 基本信息卡片
function BasicInfoCard({ student }) {
  return (
    <div className="bg-white rounded shadow-sm border border-gray-100 p-5">
      <div className="flex items-center gap-2 mb-5">
        <span className="w-1 h-4 bg-[#1890ff] rounded" />
        <h3 className="text-[15px] font-bold text-gray-900">
          基本信息
        </h3>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-x-8 gap-y-5 text-sm">
        <InfoItem
          label="姓名"
          value={student.name}
        />
        <InfoItem
          label="学号"
          value={student.studentId}
        />
        <InfoItem
          label="批次"
          value={student.batch}
        />
        <InfoItem
          label="学院"
          value={student.college}
        />
        <InfoItem
          label="班级"
          value={student.className}
        />
        <InfoItem
          label="已评价学生(已评/分配)"
          value={student.reviewedCount}
        />
        <InfoItem
          label="被评覆盖率(被评/分配)"
          value={student.coveredRate}
        />
        <InfoItem
          label="打出低分数量"
          value={student.lowScoreCount}
        />
        <InfoItem
          label="低分占比"
          value={student.lowScoreRate}
        />
      </div>
    </div>
  );
}

// 打分明细卡片
function ScoreDetailCard({
  title,
  description,
  idHeader,
  nameHeader,
  scoreHeader,
  rows,
  isMalicious = false,
  canCorrectMaliciousStatus = false,
  onOpenCorrection,
}) {
  return (
    <div className="bg-white rounded shadow-sm border border-gray-100">
      {/* 卡片标题区 */}
      <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
        <div className="text-[15px] font-bold text-[#1890ff]">
          {title}
        </div>

        {canCorrectMaliciousStatus && (
          <button
            onClick={onOpenCorrection}
            className={isMalicious ? 'px-3 py-1.5 border border-red-200 bg-red-50 text-red-500 rounded text-sm hover:bg-red-100' : 'px-3 py-1.5 border border-orange-200 bg-orange-50 text-orange-500 rounded text-sm hover:bg-orange-100'}
          >
            {isMalicious ? '人工修正' : '恢复计入'}
          </button>
        )}
      </div>

      {/* 恶意打分提示信息 */}
      {canCorrectMaliciousStatus && isMalicious && (
        <div className="mx-6 mt-5 px-3 py-3 bg-red-50 border border-red-200 rounded text-sm text-red-600 leading-6">
          该生的打分被系统判定为“恶意打分”，下列给出的分值已全部作废。如属误判，可点击右上角进行人工修正。
        </div>
      )}

      {/* 人工修正后的恢复提示 */}
      {canCorrectMaliciousStatus && !isMalicious && (
        <div className="mx-6 mt-5 px-3 py-3 bg-orange-50 border border-orange-200 rounded text-sm text-orange-600 leading-6">
          该生低分比例已超过恶意打分阈值，但当前被人工修正为“不计入恶意打分”。如发现修正错误，可点击右上角恢复计入恶意打分。
        </div>
      )}

      {/* 卡片说明区 */}
      <div className="px-6 pt-5 pb-4 text-sm text-gray-600">
        {description}
      </div>

      {/* 卡片表格区 */}
      <div className="px-6 pb-6">
        <div className="overflow-x-auto border border-gray-200 rounded">
          <table className="w-full text-left border-collapse text-[13px] text-gray-700 whitespace-nowrap">
            <thead>
              <tr className="bg-[#fafafa] border-b border-gray-200">
                <TableHead>序号</TableHead>
                <TableHead>{idHeader}</TableHead>
                <TableHead>{nameHeader}</TableHead>
                <TableHead>所属班级</TableHead>
                <TableHead>{scoreHeader}</TableHead>
                <TableHead>任务状态</TableHead>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr
                  key={row.id}
                  className="border-b border-gray-100 hover:bg-[#f8f9fa]"
                >
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{row.studentId}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.className}</TableCell>
                  <TableCell>{row.score}</TableCell>
                  <TableCell>
                    <span className={row.status === '已完成' ? 'text-green-600' : 'text-orange-500'}>
                      {row.status}
                    </span>
                  </TableCell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// 人工修正弹窗
function MaliciousCorrectionModal({ currentIsMalicious, onClose, onConfirm }) {
  // ================= 弹窗状态 =================
  const [isMalicious, setIsMalicious] = useState(currentIsMalicious ? '是' : '否');

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/35">
      <div className="w-[560px] bg-white rounded shadow-[0_8px_28px_rgba(0,0,0,0.18)]">
        {/* 弹窗标题栏 */}
        <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center">
          <h3 className="text-[15px] font-bold text-gray-900">
            人工修正
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        </div>

        {/* 兜底修正机制说明 */}
        <div className="px-6 pt-5">
          <div className="px-3 py-3 bg-[#eaf4ff] border border-[#d3e8ff] rounded text-sm text-[#1677ff] leading-6">
            系统兜底修正机制说明：若辅导员经查明，该学生的大量低分给分属于合理或特殊情况（误判），可在此处手动修正“不计入恶意打分”。修正后，该生之前打出的所有互评分数将重新生效并参与全班结算。
          </div>
        </div>

        {/* 修正表单 */}
        <div className="px-6 py-6 space-y-5">
          <div>
            <div className="text-sm text-gray-800 mb-3">
              选择是否计入恶意打分：
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="isMalicious"
                  value="是"
                  checked={isMalicious === '是'}
                  onChange={(event) => setIsMalicious(event.target.value)}
                  className="mt-1"
                />
                <span>
                  是（维持或恢复系统判定，该生打分全部失效作废）
                </span>
              </label>

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="isMalicious"
                  value="否"
                  checked={isMalicious === '否'}
                  onChange={(event) => setIsMalicious(event.target.value)}
                  className="mt-1"
                />
                <span>
                  否（辅导员特批修正，选中后该生打分将恢复有效）
                </span>
              </label>
            </div>
          </div>

          {isMalicious === '否' && (
            <label className="block">
              <span className="block text-sm text-gray-800 mb-2">
                修正说明：
              </span>
              <textarea
                placeholder="请输入修正原因和核实情况"
                className="w-full h-[90px] border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:border-[#1890ff] resize-none"
              />
            </label>
          )}
        </div>

        {/* 弹窗底部按钮区 */}
        <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50"
          >
            取消
          </button>
          <button
            onClick={() => onConfirm(isMalicious)}
            className="px-5 py-2 bg-[#1890ff] text-white rounded text-sm hover:bg-[#096dd9]"
          >
            确定
          </button>
        </div>
      </div>
    </div>
  );
}

// 基本信息展示项
function InfoItem({ label, value }) {
  return (
    <div>
      <div className="text-gray-400 mb-1">
        {label}
      </div>
      <div className="text-gray-800">
        {value}
      </div>
    </div>
  );
}

// 表格表头
function TableHead({ children }) {
  return (
    <th className="py-3 px-4 font-medium text-gray-800">
      {children}
    </th>
  );
}

// 表格单元格
function TableCell({ children }) {
  return (
    <td className="py-3 px-4">
      {children}
    </td>
  );
}
